import {
  CHANNELS,
  CHANNEL_ORDER,
  type StepMetaShape,
  UTILITY_STEPS,
} from "@/lib/apps/autoleads/channels"
import { extractRefs } from "@/lib/apps/autoleads/template-syntax"

import type { MailingTemplateKey } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type {
  PipelineStep,
  PipelineStepType,
} from "@/nango/types/apps.autoleads/autoleads/MailingPipeline"

// Shared vocabulary of a sequence step: its icon, its tone, the translation
// keys naming it and its copy family. Both the sequence editor and the task
// board read this map, so a LinkedIn step can never look like one thing while
// being authored and another while being worked. Derived from the channel
// registry: one spread per channel, plus the utility steps.
//
// `satisfies` enforces that every step type is covered.
export const STEP_META = {
  ...CHANNELS.email.steps,
  ...CHANNELS.phone.steps,
  ...CHANNELS.linkedin.steps,
  ...CHANNELS.instagram.steps,
  ...CHANNELS.whatsapp.steps,
  ...UTILITY_STEPS,
} as const satisfies Record<PipelineStepType, StepMetaShape>

export type StepMeta = (typeof STEP_META)[PipelineStepType]

// The translation keys STEP_META can ask for, so a helper taking `tr` accepts
// any of the maps that spread STEP_LABEL_T without widening to `string`.
export type StepLabelKey = (typeof STEP_META)[PipelineStepType]["labelKey"]

// The order a sequence usually grows in, which is also the picker's order:
// channel by channel, in registry order. No file upload: that step follows an
// email's @file_upload (see syncFileSteps).
export const STEP_TYPE_ORDER: readonly PipelineStepType[] =
  CHANNEL_ORDER.flatMap(
    (channel) => Object.keys(CHANNELS[channel].steps) as PipelineStepType[],
  )

export const MAX_STEPS = 20
export const MAX_DELAY_DAYS = 60
export const DEFAULT_INSERT_DELAY_DAYS = 3
// A check the day after the invitation, not three days later: the point is to
// notice the acceptance quickly, not to wait out the whole cadence again.
export const DEFAULT_CHECK_DELAY_DAYS = 1
export const DEFAULT_CHECK_INTERVAL_DAYS = 3
export const MIN_CHECK_INTERVAL_DAYS = 1
export const MAX_CHECK_INTERVAL_DAYS = 30
export const DEFAULT_MAX_CHECKS = 5

// The step's own ordinal within its family, or 0 when the family is unnumbered.
// Email and LinkedIn-message steps are counted independently, matching the two
// copy-slot families exactly.
export function familyIndex(steps: PipelineStep[], index: number): number {
  const step = steps[index]
  if (step === undefined || !STEP_META[step.type].numbered) return 0
  let seen = 0
  for (let i = 0; i <= index; i++) {
    if (steps[i]?.type === step.type) seen++
  }
  return seen
}

// The step's name as any read-only view should print it: never two identical
// rows. `familyIndex` only numbers the families that carry drafted copy, so a
// sequence holding two calls rendered "Appel" twice in the statistics panel, and
// the reader had no way to tell the first call from the last one.
export function stepDisplayLabel(
  steps: PipelineStep[],
  index: number,
  tr: (key: StepLabelKey) => string,
): string {
  const step = steps[index]
  if (step === undefined) return ""
  const meta = STEP_META[step.type]
  const ordinal = meta.numbered
    ? familyIndex(steps, index)
    : repeatOrdinal(steps, index)
  return ordinal ? `${tr(meta.labelKey)} ${ordinal}` : tr(meta.labelKey)
}

// The step's rank among its own type, or 0 when that type occurs only once: a
// lone call is just "Appel", and numbering it would be noise.
function repeatOrdinal(steps: PipelineStep[], index: number): number {
  const step = steps[index]
  if (step === undefined) return 0
  const sameType = steps.filter((other) => other.type === step.type)
  if (sameType.length < 2) return 0
  return steps.slice(0, index + 1).filter((other) => other.type === step.type)
    .length
}

// The step types carrying drafted copy, and hence a template block and a
// preview card. Everything else (a call, an invitation) is a bare action.
export type CopyStepType = {
  [T in PipelineStepType]: (typeof STEP_META)[T]["copy"] extends null
    ? never
    : T
}[PipelineStepType]

export function isCopyStep(type: PipelineStepType | ""): type is CopyStepType {
  return type !== "" && STEP_META[type].copy !== null
}

// Every copy-bearing step type, in picker order: the template families a
// sequence can own, emails first.
export const COPY_STEP_TYPES: readonly CopyStepType[] =
  STEP_TYPE_ORDER.filter(isCopyStep)

// Whether the step's drafted copy opens on a subject line (an email).
export function stepHasSubject(type: PipelineStepType): boolean {
  return STEP_META[type].copy?.hasSubject ?? false
}

// The template key of the `ordinal`-th step of a copy family ("step2", "li1").
export function templateKey(
  type: CopyStepType,
  ordinal: number,
): MailingTemplateKey {
  return `${STEP_META[type].copy.prefix}${ordinal}`
}

// The template block key of a step, or null when it carries no copy. Steps of
// a family sharing its slot all read its first block.
export function templateKeyForStep(
  steps: PipelineStep[],
  index: number,
): MailingTemplateKey | null {
  const step = steps[index]
  if (step === undefined || !isCopyStep(step.type)) return null
  if (familySharesSlot(steps, step.type)) return templateKey(step.type, 1)
  return templateKey(step.type, familyIndex(steps, index))
}

// Whether every step of `type`'s family reads one shared block (calls, unless
// the sequence gave each call its own script). Mirrors `family_shares_slot`.
export function familySharesSlot(
  steps: PipelineStep[],
  type: CopyStepType,
): boolean {
  return (
    STEP_META[type].copy.shareable &&
    !steps.some((step) => step.type === type && step.script_per_call)
  )
}

// How many distinct blocks (and drafted slots) `type`'s family owns.
export function copySlotCount(
  steps: PipelineStep[],
  type: CopyStepType,
): number {
  const count = copyStepCount(steps, type)
  return familySharesSlot(steps, type) ? Math.min(count, 1) : count
}

// One entry per step, aligned with `steps`: its template key or null. Computed
// once per render so a card never recounts the list.
export function templateKeys(
  steps: PipelineStep[],
): (MailingTemplateKey | null)[] {
  return steps.map((_, index) => templateKeyForStep(steps, index))
}

// How many steps of one copy family the sequence holds.
export function copyStepCount(
  steps: PipelineStep[],
  type: CopyStepType,
): number {
  return steps.filter((step) => step.type === type).length
}

export function hasLinkedinInvite(steps: PipelineStep[]): boolean {
  return steps.some((step) => step.type === "linkedin_invite")
}

// A LinkedIn message can only be sent to a connection, and nothing tells us the
// invitation was accepted unless a check step asks. Without one, the message
// step never unblocks: worth saying, not worth silently fixing.
export function needsInviteCheck(steps: PipelineStep[]): boolean {
  return (
    hasLinkedinInvite(steps) &&
    copyStepCount(steps, "linkedin_message") > 0 &&
    !steps.some((step) => step.type === "linkedin_invite_check")
  )
}

export function checkIntervalDays(step: PipelineStep): number {
  return step.check_interval_days ?? DEFAULT_CHECK_INTERVAL_DAYS
}

// The reference an email template writes to place the contact's file.
export const FILE_REF = "@file_upload"

// Index of the file upload step feeding the email at `emailIndex`, or -1. The
// file belongs to the first email after it, so the search stops at the
// previous email.
export function fileStepForEmail(
  steps: PipelineStep[],
  emailIndex: number,
): number {
  for (let index = emailIndex - 1; index >= 0; index--) {
    const type = steps[index]?.type
    if (type === "email") return -1
    if (type === "file_upload") return index
  }
  return -1
}

// Steps where every email whose template places the file has a file upload
// step right before it, and no other email has one. The email's wait moves to
// the file step (the rep is asked for the file when the email was due) and the
// email goes out as soon as the file is in; removing the step gives it back.
export function syncFileSteps(
  steps: PipelineStep[],
  template: Partial<Record<string, string>>,
): PipelineStep[] {
  const next = [...steps]
  for (let index = next.length - 1; index >= 0; index--) {
    if (next[index]?.type !== "email") continue
    const key = templateKeyForStep(next, index)
    const placed = key !== null && hasFileRef(template[key] ?? "")
    const fileIndex = fileStepForEmail(next, index)
    if (placed && fileIndex < 0) {
      const email = next[index]
      next.splice(
        index,
        1,
        { type: "file_upload", delay_days: email.delay_days },
        { ...email, delay_days: 0 },
      )
    } else if (!placed && fileIndex >= 0) {
      const fileStep = next[fileIndex]
      const email = next[index]
      next[index] = {
        ...email,
        delay_days: Math.min(
          MAX_DELAY_DAYS,
          email.delay_days + fileStep.delay_days,
        ),
      }
      next.splice(fileIndex, 1)
    }
  }
  return next
}

function hasFileRef(body: string): boolean {
  return extractRefs(body).some((ref) => `@${ref.slug}` === FILE_REF)
}
